import {AgentAgencyClaim} from './AgentAgencyClaim';
import {Async, toEither} from 'Core/models/Async';
import {head, map as mapArray} from 'fp-ts/lib/Array';
import {constant} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Scope} from 'Shared/constants/Scope';
import unionize, {ofType, UnionOf} from 'unionize';
import {Agent} from 'Users/models/Agent';
import {AgentClaims} from 'Users/models/AgentClaims';
import {MyAccessPortalClaims} from 'Users/models/MyAccessPortalClaims';
import {RoleBase, ExclusivityStatus} from 'Users/api';

export enum UserTypeModelKey {
  AgentSite = 'Users.API.Dtos.Agent.AgentUserTypeDto, Users.API',
  EnrollTrack = 'Users.API.Dtos.EnrollTrack.EnrollTrackUserTypeDto, Users.API',
  BosTools = 'Users.API.Dtos.BusinessOperationsSupport.BusinessOperationsSupportUserTypeDto, Users.API',
  Beneficiary = 'Users.API.Dtos.Beneficiary.BeneficiaryUserTypeDto, Users.API',
  Messaging = 'Users.API.Dtos.Messaging.MessagingUserTypeDto, Users.API',
  ApplicationEntry = 'Users.API.Dtos.ApplicationEntry.ApplicationEntryUserTypeDto, Users.API',
}

export enum UserTypeClaimKey {
  AgentSite = 'Renaissance.Authorization.Claims.AgentClaim.AgentUserTypeClaim, Renaissance.Authorization',
  MyAccessPortal = 'Renaissance.Authorization.Claims.PortalClaim.PortalUserTypeClaim, Renaissance.Authorization',
  EnrollTrack = 'Renaissance.Authorization.Claims.EnrollTrackClaim.EnrollTrackUserTypeClaim, Renaissance.Authorization',
  Beneficiary = 'Renaissance.Authorization.Claims.BeneficiaryClaim.BeneficiaryUserTypeClaim, Renaissance.Authorization',
  BOSTools = 'Renaissance.Authorization.Claims.BOSToolsClaim.BusinessOperationsSupportUserTypeClaim, Renaissance.Authorization',
  Messaging = 'Renaissance.Authorization.Claims.MessagingClaim.MessagingUserTypeClaim, Renaissance.Authorization',
  ApplicationEntry = 'Renaissance.Authorization.Claims.ApplicationEntry.ApplicationEntryUserTypeClaim, Renaissance.Authorization',
}

export const getAgentModelClaims = (userTypeClaims: UserTypeModelUnion[]) =>
  pipe(
    userTypeClaims,
    mapArray(
      UserTypeModel.match({
        [UserTypeModelKey.AgentSite]: agentClaims => O.some(agentClaims),
        default: constant(O.none),
      })
    ),
    head,
    O.flatten
  );
export const getAgentClaims = (userTypeClaims: UserTypeClaimsUnion[]) =>
  pipe(
    userTypeClaims,
    mapArray(
      UserTypeClaims.match({
        [UserTypeClaimKey.AgentSite]:
          process.env.REACT_APP_TOGGLE_AGENT_ACCESS === 'true' ? agentClaims => O.some(agentClaims) : constant(O.none),
        default: constant(O.none),
      })
    ),
    head,
    O.flatten
  );
export type StandardClaims = {
  isNewYork: boolean;
  isAhl: boolean;
  statusId: number;
  scopes: Scope[];
};
export type AgentProfile = {
  impersonatedAgent?: Agent;
  impersonatedAgentClaims?: AsyncUserClaims;
};
export type EnrollTrackClaims = {
  position: {
    id: number;
    name: string;
  };
  scopes: Scope[];
  roles: RoleBase[];
  firstName: string;
  LastName: string;
  username: string;
  impersonatedEnrollTrackClaim: ImpersonatedEnrollTrackUserClaims;
  agencyAssociations: AgentAgencyClaim[];
  exclusivityStatus: {
    id: number;
    name: string;
  };
};

export type BeneficiaryClaims = {
  scopes: Scope[];
  roles: RoleBase[];
};

export type BOSToolsClaims = {
  scopes: Scope[];
  roles: RoleBase[];
};

export type MessagingClaims = {
  scopes: Scope[];
  roles: RoleBase[];
};

export type ApplicationEntryClaims = {
  scopes: Scope[];
  roles: RoleBase[];
};

export const UserTypeClaims = unionize(
  {
    [UserTypeClaimKey.AgentSite]: ofType<AgentClaims>(),
    [UserTypeClaimKey.MyAccessPortal]: ofType<MyAccessPortalClaims>(),
    [UserTypeClaimKey.EnrollTrack]: ofType<EnrollTrackClaims>(),
    [UserTypeClaimKey.Beneficiary]: ofType<BeneficiaryClaims>(),
    [UserTypeClaimKey.BOSTools]: ofType<BOSToolsClaims>(),
    [UserTypeClaimKey.Messaging]: ofType<MessagingClaims>(),
    [UserTypeClaimKey.ApplicationEntry]: ofType<ApplicationEntryClaims>(),
    Empty: ofType<{}>(),
  },
  {
    tag: '$type',
  }
);
export const UserTypeModel = unionize(
  {
    [UserTypeModelKey.AgentSite]: ofType<AgentClaims>(),
    [UserTypeModelKey.EnrollTrack]: ofType<EnrollTrackClaims>(),
    [UserTypeModelKey.BosTools]: ofType<BOSToolsClaims>(),
    [UserTypeModelKey.Beneficiary]: ofType<BeneficiaryClaims>(),
    [UserTypeModelKey.Messaging]: ofType<MessagingClaims>(),
    [UserTypeModelKey.ApplicationEntry]: ofType<ApplicationEntryClaims>(),
    Empty: ofType<{}>(),
  },
  {
    tag: '$type',
  }
);
export type UserDetailsModel = {
  id: string;
  firstName: string;
  email: string;
  lastName: string;
  userName: string;
  mobilePhone: string;
  isImpersonating?: boolean;
  originalUserName?: string;
  hasAcceptedTermsAndConditions: boolean;
  userTypes: UserTypeModelUnion[];
  scopes: Scope[];
  regionCodes: [
    {
      id: number;
      value: string;
    }
  ];
  recruiterCodes: [
    {
      id: string;
      value: string;
      isNewYork: boolean;
    }
  ];
  channel: {
    name: string;
    id: number;
  };
  exclusivityStatus: ExclusivityStatus;
};
export type UserTypeClaimsUnion = UnionOf<typeof UserTypeClaims>;
export type UserTypeModelUnion = UnionOf<typeof UserTypeModel>;
export type ImpersonatedAgentUserClaims = UserClaims & AgentClaims;
export type ImpersonatedEnrollTrackUserClaims = UserClaims & EnrollTrackClaims;
export type LegalDocument = {
  id: string;
  legalDocumentType: {
    id: number;
    name: string;
  };
  versionNumber: number;
};
export type UserClaims = {
  id: string;
  firstName: string;
  email: string;
  lastName: string;
  userName: string;
  mobilePhone: string;
  isImpersonating?: boolean;
  originalUserName?: string;
  hasAcceptedTermsAndConditions: boolean;
  userTypes: UserTypeClaimsUnion[];
  scopes: Scope[];
  unacceptedLegalDocuments: LegalDocument[];
  shouldResetPassword: boolean;
  exclusivityStatus: {
    id: number;
    name: string;
  };
  regionCodes: [
    {
      id: number;
      value: string;
    }
  ];
  recruiterCodes: [
    {
      id: string;
      value: string;
      isNewYork: boolean;
    }
  ];
  channel: {
    name: string;
    id: number;
  };
};
export type AsyncUserClaims = Async<UserClaims>;
export const toOption = (userClaimsUnion: AsyncUserClaims) => O.fromEither(toEither(userClaimsUnion));
